import { useEffect, useState } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import AdminLayout from '../components/AdminLayout';
import { IR_REPORTS_BY_KEY } from '@/data/irReports';
import { REPORTS_BY_KEY } from '@/data/capitalReports';
import { applyReportOverride } from '@/data/reportContent';
import { FileText, Save, CheckCircle2, RotateCcw, Info, Plus, Trash2, Table as TableIcon, GitBranch, ScrollText } from 'lucide-react';

const GOALS = ['G1', 'G2', 'G3', 'G4'];
const RISKS: [string, string][] = [
  ['R1', 'Critical Risk'], ['R2', 'Very High Risk'], ['R3', 'High Risk'], ['R4', 'Medium Risk'],
  ['R5', 'Semi-Ready'], ['R6', 'Investor-Ready'], ['R7', 'Exceptional'],
];
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

const inp = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[var(--brand-soft)] focus:border-[var(--brand)]';
const lbl = 'text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1';

function buildOverride(rep: any, set: 'ir' | 'capital') {
  const ov: any = { profile: rep.profile };
  if (set === 'ir') ov.tagline = rep.tagline; else ov.intro = rep.intro;
  ov.sections = (rep.sections || []).map((sec: any) => ({
    title: sec.title,
    blocks: (sec.blocks || []).map((b: any) => {
      if (b.kind === 'paragraph' || b.kind === 'callout') return { text: b.text };
      if (b.kind === 'bullets') return { items: b.items };
      if (b.kind === 'steps') return { items: (b.items || []).map((it: any) => ({ title: it.title, sub: it.sub || [] })) };
      return {}; // table / path — keep index alignment, no override
    }),
  }));
  return ov;
}

export default function AdminReportContent() {
  const [set, setSet] = useState<'ir' | 'capital'>('ir');
  const [goal, setGoal] = useState('G1');
  const [risk, setRisk] = useState('R1');
  const [docData, setDocData] = useState<{ ir: any; capital: any }>({ ir: {}, capital: {} });
  const [rep, setRep] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const key = `${goal}${risk}`;
  const isEdited = !!docData[set]?.[key];

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'reportContent', 'current'));
        const d = snap.exists() ? snap.data() : {};
        setDocData({ ir: d.ir || {}, capital: d.capital || {} });
      } catch (e) { console.error('load reportContent failed', e); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    const map = set === 'ir' ? IR_REPORTS_BY_KEY : REPORTS_BY_KEY;
    const def = (map as any)[key];
    setRep(def ? clone(applyReportOverride(def, docData[set]?.[key])) : null);
  }, [set, goal, risk, docData]);

  const mutate = (fn: (n: any) => void) => setRep((r: any) => { const n = clone(r); fn(n); return n; });

  const handleSave = async () => {
    if (!rep) return;
    setSaving(true);
    try {
      const ov = buildOverride(rep, set);
      // Send the FULL {ir, capital} maps (backend merges by top-level field), so
      // updating one report never wipes the others in the same set.
      const next: any = { ...docData, [set]: { ...docData[set], [key]: ov } };
      await saveAdminConfig('reportContent/current', { ir: next.ir || {}, capital: next.capital || {} });
      setDocData(next);
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'reportContent/current', { report: `${set}:${key}` });
    } catch (e: any) {
      showAlert(e?.message || 'Save failed. Please try again.');
    } finally { setSaving(false); }
  };

  const resetReport = async () => {
    setSaving(true);
    try {
      const next: any = { ...docData, [set]: { ...docData[set], [key]: null } };
      await saveAdminConfig('reportContent/current', { ir: next.ir || {}, capital: next.capital || {} });
      setDocData(next);
      setSavedAt(new Date().toLocaleTimeString());
    } catch { showAlert('Reset failed. Check console.'); }
    finally { setSaving(false); }
  };

  return (
    <AdminLayout
      section="Content"
      title="Report Content"
      icon={ScrollText}
      subtitle="The copy that fills the Capital Readiness report clients download."
      spaced
      actions={<>
        {savedAt && <span className="ax-saved"><CheckCircle2 /> Saved {savedAt}</span>}
        {isEdited && <button onClick={resetReport} disabled={saving} className="btn"><RotateCcw /> Reset to default</button>}
        <button onClick={handleSave} disabled={saving || !rep} className="btn btn-pri">
          <Save /> {saving ? 'Saving…' : 'Save report'}
        </button>
      </>}
    >
      <div className="flex items-start gap-3 rounded-xl border border-[var(--brand-soft)] bg-[var(--brand-soft)]/60 px-4 py-3">
        <Info className="w-4 h-4 text-[var(--brand-d)] mt-0.5 shrink-0" />
        <p className="text-sm text-[var(--brand-d)]/80">Edit the wording of the founder reports. Changes apply to both the on-screen report and the downloadable PDF, and never affect scoring. Tables and journey diagrams are managed in code (shown read-only). Blank fields fall back to the built-in text.</p>
      </div>

      {/* Selectors */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {(['ir', 'capital'] as const).map(s => (
            <button key={s} onClick={() => setSet(s)} className={`px-4 py-2 text-sm font-bold ${set === s ? 'bg-[var(--brand)] text-[var(--brand-on)]' : 'text-slate-600 hover:bg-slate-50'}`}>
              {s === 'ir' ? 'IR Reports' : 'Capital Reports'}
            </button>
          ))}
        </div>
        <select value={goal} onChange={e => setGoal(e.target.value)} className={`${inp} w-auto`}>
          {GOALS.map(g => <option key={g} value={g}>Goal {g.slice(1)}</option>)}
        </select>
        <select value={risk} onChange={e => setRisk(e.target.value)} className={`${inp} w-auto`}>
          {RISKS.map(([r, label]) => <option key={r} value={r}>{r} · {label}</option>)}
        </select>
        {isEdited && <span className="ml-auto text-xs font-bold text-[var(--brand-d)] bg-[var(--brand-soft)] px-2.5 py-1 rounded-full">Edited</span>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[var(--brand)] border-t-transparent rounded-full animate-spin" /></div>
      ) : !rep ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center text-sm text-slate-400">No report for {key}.</div>
      ) : (
        <>
          {/* Header fields */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="text-xs font-bold text-slate-400 mb-3">{set === 'ir' ? rep.pillar : rep.domain} · {rep.stage}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={lbl}>Profile / persona</label><input className={inp} value={rep.profile || ''} onChange={e => mutate(n => { n.profile = e.target.value; })} /></div>
              <div><label className={lbl}>{set === 'ir' ? 'Tagline' : 'Intro line'}</label><input className={inp} value={(set === 'ir' ? rep.tagline : rep.intro) || ''} onChange={e => mutate(n => { if (set === 'ir') n.tagline = e.target.value; else n.intro = e.target.value; })} /></div>
            </div>
          </div>

          {/* Sections */}
          {(rep.sections || []).map((sec: any, i: number) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-[var(--brand)] to-[var(--brand-d)]" />
              <div className="p-5 space-y-4">
                <div>
                  <label className={lbl}>Section title</label>
                  <input className={`${inp} font-semibold`} value={sec.title || ''} onChange={e => mutate(n => { n.sections[i].title = e.target.value; })} />
                </div>

                {(sec.blocks || []).map((b: any, j: number) => {
                  if (b.kind === 'paragraph' || b.kind === 'callout') {
                    return (
                      <div key={j}>
                        <label className={lbl}>{b.kind === 'callout' ? 'Callout' : 'Paragraph'}</label>
                        <textarea rows={b.kind === 'callout' ? 2 : 3} className={`${inp} resize-y ${b.kind === 'callout' ? 'bg-[var(--brand-soft)]/40' : ''}`} value={b.text || ''} onChange={e => mutate(n => { n.sections[i].blocks[j].text = e.target.value; })} />
                      </div>
                    );
                  }
                  if (b.kind === 'bullets') {
                    return (
                      <div key={j}>
                        <label className={lbl}>Bullet list</label>
                        <div className="space-y-2">
                          {(b.items || []).map((it: string, k: number) => (
                            <div key={k} className="flex items-center gap-2">
                              <span className="text-slate-300 text-sm">•</span>
                              <input className={inp} value={it} onChange={e => mutate(n => { n.sections[i].blocks[j].items[k] = e.target.value; })} />
                              <button onClick={() => mutate(n => { n.sections[i].blocks[j].items.splice(k, 1); })} className="text-slate-300 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ))}
                          <button onClick={() => mutate(n => { n.sections[i].blocks[j].items.push(''); })} className="text-xs font-bold text-[var(--brand-d)] inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add bullet</button>
                        </div>
                      </div>
                    );
                  }
                  if (b.kind === 'steps') {
                    return (
                      <div key={j}>
                        <label className={lbl}>Steps</label>
                        <div className="space-y-3">
                          {(b.items || []).map((it: any, k: number) => (
                            <div key={k} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                              <input className={`${inp} font-semibold mb-2`} value={it.title || ''} onChange={e => mutate(n => { n.sections[i].blocks[j].items[k].title = e.target.value; })} placeholder={`Step ${k + 1}`} />
                              <div className="space-y-1.5 pl-3">
                                {(it.sub || []).map((s: string, si: number) => (
                                  <div key={si} className="flex items-center gap-2">
                                    <span className="text-slate-300 text-xs">–</span>
                                    <input className={inp} value={s} onChange={e => mutate(n => { n.sections[i].blocks[j].items[k].sub[si] = e.target.value; })} />
                                    <button onClick={() => mutate(n => { n.sections[i].blocks[j].items[k].sub.splice(si, 1); })} className="text-slate-300 hover:text-red-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                                  </div>
                                ))}
                                <button onClick={() => mutate(n => { const it2 = n.sections[i].blocks[j].items[k]; it2.sub = it2.sub || []; it2.sub.push(''); })} className="text-[11px] font-bold text-[var(--brand-d)] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add sub-point</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (b.kind === 'table') {
                    return (
                      <div key={j}>
                        <label className={`${lbl} flex items-center gap-1`}><TableIcon className="w-3 h-3" /> Table (managed in code)</label>
                        <div className="overflow-x-auto rounded-lg border border-slate-100">
                          <table className="w-full text-xs">
                            <thead><tr className="bg-slate-50">{(b.headers || []).map((h: string, hi: number) => <th key={hi} className="text-left px-3 py-2 font-bold text-slate-500">{h}</th>)}</tr></thead>
                            <tbody>{(b.rows || []).map((row: any[], ri: number) => <tr key={ri} className="border-t border-slate-100">{row.map((c: any, ci: number) => <td key={ci} className="px-3 py-2 text-slate-600">{c?.text}</td>)}</tr>)}</tbody>
                          </table>
                        </div>
                      </div>
                    );
                  }
                  if (b.kind === 'path') {
                    return (
                      <div key={j}>
                        <label className={`${lbl} flex items-center gap-1`}><GitBranch className="w-3 h-3" /> Journey path (managed in code)</label>
                        <div className="flex flex-wrap gap-1.5">
                          {(b.steps || []).map((s: string, si: number) => (
                            <span key={si} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${si === b.currentIndex ? 'bg-[var(--brand)] text-[var(--brand-on)]' : 'bg-slate-100 text-slate-500'}`}>{s}</span>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </AdminLayout>
  );
}
